import apiClient from '../apiClient';
import { API_ENDPOINTS } from '../endpoints';

/* =========================================================
   TYPES
========================================================= */

export interface HousePlayer {
  id: string;
  houseId: string;
  displayName: string;
  reactionBias: number;
  missBias: number;
  claimBias: number;
  chatBias: number;
  avatarSeed: number;
  gamesPlayed: number;
  gamesWon: number;
  balance: number;
  userActive: boolean;
  isRetired: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface HousePlayerStats {
  total: number;
  retired: number;
  active: number;
  totalGames: number;
  totalWins: number;
  winRate: number;
  totalBalance: number;
}

export interface HousePlayerSettings {
  _id: string;
  key: string;
  enabled: boolean;

  /**
   * ⭐ Per-room players-per-room map.
   * Key = roomId, Value = target players for that room.
   * A room without an entry gets 0 house players.
   */
  playersPerRoomByRoomId: Record<string, number>;

  applyToRoomIds: string[];
  applyToSuperBingo: boolean;
  fillDelayMs: number;
  maxHouseShareOfCartelas: number;
  skipWhenHumanSeatsAtLeast: number;
  startingBalance: number;
  autoTopUp: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateHousePlayerSettingsInput {
  enabled?: boolean;
  playersPerRoomByRoomId?: Record<string, number>;
  applyToRoomIds?: string[];
  applyToSuperBingo?: boolean;
  fillDelayMs?: number;
  maxHouseShareOfCartelas?: number;
  skipWhenHumanSeatsAtLeast?: number;
  startingBalance?: number;
  autoTopUp?: boolean;
}

export interface CreateHousePlayersInput {
  names: string[];
}

export interface CreateHousePlayersResult {
  created: number;
  skipped: number;
  failed: number;
  results: Array<{ name: string; status: string; reason?: string; id?: string }>;
}

/**
 * ⭐ One row from GET /admin/house-players/rooms.
 * Used only to populate the room dropdown when the admin
 * opens the picker — never fetched on tab open.
 */
export interface HouseRoomOption {
  roomId: string;
  label: string;
  status: 'waiting' | 'running' | 'finished' | string;
  isSuperBingo: boolean;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
  message?: string;
}

/* =========================================================
   SERVICE
========================================================= */

class HousePlayerService {
  async list(params: {
    page?: number;
    limit?: number;
    search?: string;
    retired?: 'all' | 'active' | 'retired';
  }): Promise<{ players: HousePlayer[]; pagination: Pagination }> {
    const res = await apiClient.get<
      ApiEnvelope<{ players: HousePlayer[]; pagination: Pagination }>
    >(API_ENDPOINTS.HOUSE_PLAYERS.LIST, { params });
    return res.data.data;
  }

  async stats(): Promise<HousePlayerStats> {
    const res = await apiClient.get<ApiEnvelope<HousePlayerStats>>(
      API_ENDPOINTS.HOUSE_PLAYERS.STATS
    );
    return res.data.data;
  }

  async getSettings(): Promise<HousePlayerSettings> {
    const res = await apiClient.get<ApiEnvelope<HousePlayerSettings>>(
      API_ENDPOINTS.HOUSE_PLAYERS.SETTINGS
    );
    return res.data.data;
  }

  async updateSettings(
    input: UpdateHousePlayerSettingsInput
  ): Promise<HousePlayerSettings> {
    const res = await apiClient.put<ApiEnvelope<HousePlayerSettings>>(
      API_ENDPOINTS.HOUSE_PLAYERS.SETTINGS,
      input
    );
    return res.data.data;
  }

  async create(
    input: CreateHousePlayersInput
  ): Promise<CreateHousePlayersResult> {
    const res = await apiClient.post<ApiEnvelope<CreateHousePlayersResult>>(
      API_ENDPOINTS.HOUSE_PLAYERS.CREATE,
      input
    );
    return res.data.data;
  }

  async update(
    id: string,
    input: Partial<
      Omit<HousePlayer, 'id' | 'houseId' | 'createdAt' | 'updatedAt'>
    >
  ): Promise<HousePlayer> {
    const res = await apiClient.put<ApiEnvelope<HousePlayer>>(
      API_ENDPOINTS.HOUSE_PLAYERS.UPDATE(id),
      input
    );
    return res.data.data;
  }

  async toggleRetired(
    id: string
  ): Promise<{ id: string; isRetired: boolean }> {
    const res = await apiClient.patch<
      ApiEnvelope<{ id: string; isRetired: boolean }>
    >(API_ENDPOINTS.HOUSE_PLAYERS.TOGGLE(id));
    return res.data.data;
  }

  async remove(id: string): Promise<void> {
    await apiClient.delete(API_ENDPOINTS.HOUSE_PLAYERS.DELETE(id));
  }

  /**
   * ⭐ Fetch the list of available rooms.
   *
   * Only called when the admin OPENS the room picker (or
   * clicks "Add room"). Never called on tab open, so the
   * settings card costs zero extra requests on first paint.
   */
  async listRooms(): Promise<HouseRoomOption[]> {
    const res = await apiClient.get<ApiEnvelope<HouseRoomOption[]>>(
      API_ENDPOINTS.HOUSE_PLAYERS.ROOMS
    );
    return res.data.data;
  }
}

const housePlayerService = new HousePlayerService();
export default housePlayerService;